import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserPriviliges, userModeChange, updateUserIsInstructor, logout } from 'Store/UserInfo/Actions';
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';
import { Navbar, Nav, Container, Image, Dropdown, NavLink, Button, Alert, Modal } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ProfileButton from './ProfileButton';
import Chip from '@mui/material/Chip';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LoginButton from 'Screens_old/Layout/Common/Components/LoginButton';
import LoginPage from 'Screens_old/Layout/Common/Components/LoginPage';
import { enableRoleDisplayInNavbar } from 'Constants';
import trust_edge_logo from "Screens_old/Footer/assets/trust_edge_footer_logo.webp";
import log from "loglevel";

const NavigationBar = ({ handleShowLogin, handleHideLogin }) => {
  const userInfo = useSelector(state => state.userInfo);
  const userCourseRole = useSelector(state => state.userCourseRole);
  const isAuthenticated = userInfo.token !== null;
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [showDrawer, setShowDrawer] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [modalReady, setModalReady] = useState(false);

  // Initialize modal readiness
  useEffect(() => {
    setModalReady(true);
  }, []);

  useEffect(() => {
    async function getPrivileges() {
      let is_instructor = await getUserPriviliges();
      dispatch(updateUserIsInstructor(is_instructor?.is_instructor));
    }

    if (isAuthenticated && !userInfo.is_instructor) getPrivileges();
  }, [isAuthenticated, dispatch, userInfo.is_instructor]);

  useEffect(() => {
    // Lock body scroll when drawer is open
    if (showDrawer) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [showDrawer]);

  // Handle scrolling to section when navigating with hash
  useEffect(() => {
    if (location.hash && location.pathname === '/') {
      const sectionId = location.hash.substring(1); // Remove the '#' prefix
      // Use setTimeout to ensure the DOM is ready
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const yOffset = -80; // Offset for fixed navbar
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [location.hash, location.pathname]);

  const handleModeChange = useCallback((modeToChange) => {
    if (modeToChange === userInfo.mode) return;
    if (modeToChange === 'S' || userInfo.is_instructor === true) {
      dispatch(userModeChange(modeToChange));
    } else {
      displayGlobalMessage('You are not an instructor!', 'error');
    }
  }, [userInfo.mode, userInfo.is_instructor, dispatch]);

  const isActiveNavLink = useCallback((to) => {
    return location.pathname === to;
  }, [location.pathname]);

  // userCourseRole is course-scoped and not persisted, so outside a course fall back to the persisted mode (non-instructors only; the Mode dropdown covers the rest).
  const isCourseRoute = location.pathname.startsWith('/courseware/course/');
  const displayRole = isCourseRoute
    ? userCourseRole
    : (!userInfo.is_instructor && userInfo.mode === 'S' ? 'STUDENT' : null);

  const toggleDrawer = useCallback(() => {
    setShowDrawer(prev => !prev);
  }, []);

  const closeDrawer = useCallback(() => {
    setShowDrawer(false);
  }, []);

  const handleSmoothScroll = useCallback((sectionId) => {
    return (e) => {
      e.preventDefault();

      // If not on home page, navigate to home with hash
      if (location.pathname !== '/') {
        navigate(`/#${sectionId}`);
        return;
      }

      // If on home page, scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        const yOffset = -80; // Offset for fixed navbar
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    };
  }, [location.pathname, navigate]);

  // const handleShowLogin = useCallback(() => {
  //   log.log("Opening login modal");
  //   // alert("Hello");
  //   // Only show modal if it's ready and not already showing
  //   if (modalReady && !showLoginModal) {
  //     // alert("World");
  //     setTimeout(() => {
  //       setShowLoginModal(true);
  //     }, 10);
  //   }
  // }, [modalReady, showLoginModal]);

  // const handleHideLogin = useCallback(() => {
  //   log.log("Closing login modal");
  //   setShowLoginModal(false);
  // }, []);

  const logoSrc = isAuthenticated
    ? '/logo192.png'
    : (process.env.REACT_APP_MODE === "trustedge" ? trust_edge_logo : '/logo192.png');

  const logoAlt = isAuthenticated
    ? 'Bodhitree Logo'
    : (process.env.REACT_APP_MODE === "trustedge" ? 'Trust Edge Logo' : 'Bodhitree Logo');

  // Custom ProfileButton for the mobile drawer
  const MobileProfileButton = ({ email, username }) => {
    return (
      <Dropdown as={Nav}>
        <Dropdown.Toggle
          className='px-2'
          as={Chip}
          icon={<AccountCircleIcon />}
          label={username ? username : email}
          variant='outlined'
        />
        <Dropdown.Menu>
          <Dropdown.Item
            as={Link}
            to='/profile'
            onClick={closeDrawer}
          >
            Profile
          </Dropdown.Item>
          <Dropdown.Item
            as={Link}
            to='/'
            onClick={async () => {
              await dispatch(logout());
              closeDrawer();
            }}
          >
            Logout
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <div className='sticky-top'>
      <Navbar className='py-2 bg-white' variant='light'>
        <Container fluid className='px-0'>
          <Navbar.Brand as={Link} to='/' className="ms-0 ps-1">
            <Image
              src={logoSrc}
              style={{ width: "110px", height: "40px", objectFit: "contain", verticalAlign: "middle" }}
              alt={logoAlt}
            />
          </Navbar.Brand>

          {/* Desktop navigation - visible on lg screens and up */}
          <div className="d-none d-lg-flex align-items-center ml-auto">
            {isAuthenticated ? (
              <>
                {enableRoleDisplayInNavbar && (
                  <>
                    {displayRole === 'STUDENT' && (
                      <Alert className='my-auto p-2 mx-2' variant='success'>STUDENT</Alert>
                    )}
                    {displayRole === 'INSTRUCTOR' && (
                      <Alert className='my-auto p-2 mx-2' variant='danger'>INSTRUCTOR</Alert>
                    )}
                    {displayRole === 'STA' && (
                      <Alert className='my-auto p-2 mx-2' variant='info'>STA</Alert>
                    )}
                    {displayRole === 'JTA' && (
                      <Alert className='my-auto p-2 mx-2' variant='warning'>JTA</Alert>
                    )}
                  </>
                )}
                {userInfo.is_instructor && (
                  <Nav>
                    <div className='my-auto px-2 mb-0 text-secondary'>Mode:</div>
                    <Dropdown onSelect={handleModeChange}>
                      <Dropdown.Toggle>
                        {userInfo.mode === 'I' ? 'Instructor' : 'Student'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item eventKey='I'>Instructor</Dropdown.Item>
                        <Dropdown.Item eventKey='S'>Student</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Nav>
                )}
                <NavLink
                  as={Link}
                  to='/courseware/courseslist'
                  className={`nav-link ${isActiveNavLink('/courseware/courseslist') ? 'text-primary' : 'text-secondary'}`}
                >
                  My Courses
                </NavLink>
                <NavLink
                  as={Link}
                  to='/courseware'
                  className={`nav-link ${isActiveNavLink('/courseware') ? 'text-primary' : 'text-secondary'}`}
                >
                  Explore
                </NavLink>
                <ProfileButton email={userInfo.email} username={userInfo.username} />
              </>
            ) : (
              <Nav className="d-flex align-items-center">
                {process.env.REACT_APP_MODE === "trustedge" && (
                  <>
                    <NavLink
                      href="#programs-we-offer"
                      className="nav-link text-secondary"
                      onClick={handleSmoothScroll('programs-we-offer')}
                    >
                      Programs
                    </NavLink>
                    <NavLink
                      href="#courses-we-offer"
                      className="nav-link text-secondary"
                      onClick={handleSmoothScroll('courses-we-offer')}
                    >
                      Courses
                    </NavLink>
                    <NavLink
                      href="#why-us"
                      className="nav-link text-secondary"
                      onClick={handleSmoothScroll('why-us')}
                    >
                      Why Us
                    </NavLink>
                    <NavLink
                      href="#testimonials"
                      className="nav-link text-secondary"
                      onClick={handleSmoothScroll('testimonials')}
                    >
                      Testimonials
                    </NavLink>
                    <NavLink
                      href="#press-coverage"
                      className="nav-link text-secondary"
                      onClick={handleSmoothScroll('press-coverage')}
                    >
                      Press Coverage
                    </NavLink>
                  </>
                )}
                <LoginButton onClick={handleShowLogin} />
              </Nav>
            )}
          </div>

          {/* Mobile toggle button */}
          <Button
            variant="light"
            className="d-lg-none ml-auto"
            onClick={toggleDrawer}
          >
            <span className="navbar-toggler-icon"></span>
          </Button>
        </Container>
      </Navbar>
      <hr className='border border-secondary border-1 opacity-50 p-0 m-0' />

      {/* Mobile drawer backdrop */}
      <div
        className={`position-fixed d-lg-none ${showDrawer ? 'd-block' : 'd-none'}`}
        style={{
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1030
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeDrawer();
          }
        }}
      />

      {/* Mobile drawer */}
      <div
        className="position-fixed bg-white d-lg-none h-100 overflow-auto"
        style={{
          top: 0,
          left: showDrawer ? '0' : '-280px',
          width: '280px',
          zIndex: 1040,
          transition: 'left 0.3s ease'
        }}
      >
        <div className="p-3 border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="m-0">Menu</h5>
            <Button variant="light" className="p-0" onClick={closeDrawer}>×</Button>
          </div>
        </div>
        <div className="p-3">
          <Nav className="flex-column">
            {isAuthenticated ? (
              <>
                {userInfo.is_instructor && userInfo.mode !== 'S' && (
                  <div className="mb-3">
                    <div className='mb-2 text-secondary'>Mode:</div>
                    <Dropdown onSelect={handleModeChange}>
                      <Dropdown.Toggle className="w-100">
                        {userInfo.mode === 'I' ? 'Instructor' : 'Student'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item eventKey='I'>Instructor</Dropdown.Item>
                        <Dropdown.Item eventKey='S'>Student</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                )}
                <NavLink
                  as={Link}
                  to='/courseware/courseslist'
                  className={`nav-link px-0 ${isActiveNavLink('/courseware/courseslist') ? 'text-primary' : 'text-secondary'}`}
                  onClick={closeDrawer}
                >
                  My Courses
                </NavLink>
                <NavLink
                  as={Link}
                  to='/courseware'
                  className={`nav-link px-0 ${isActiveNavLink('/courseware') ? 'text-primary' : 'text-secondary'}`}
                  onClick={closeDrawer}
                >
                  Explore
                </NavLink>
                <div className="mt-3">
                  <MobileProfileButton email={userInfo.email} username={userInfo.username} />
                </div>
              </>
            ) : (
              <>
                {process.env.REACT_APP_MODE === "trustedge" && (
                  <>
                    <NavLink
                      href="#programs-we-offer"
                      className="nav-link px-0 text-secondary"
                      onClick={(e) => {
                        handleSmoothScroll('programs-we-offer')(e);
                        closeDrawer();
                      }}
                    >
                      Programs
                    </NavLink>
                    <NavLink
                      href="#courses-we-offer"
                      className="nav-link px-0 text-secondary"
                      onClick={(e) => {
                        handleSmoothScroll('courses-we-offer')(e);
                        closeDrawer();
                      }}
                    >
                      Courses
                    </NavLink>
                    <NavLink
                      href="#why-us"
                      className="nav-link px-0 text-secondary"
                      onClick={(e) => {
                        handleSmoothScroll('why-us')(e);
                        closeDrawer();
                      }}
                    >
                      Why Us
                    </NavLink>
                    <NavLink
                      href="#testimonials"
                      className="nav-link px-0 text-secondary"
                      onClick={(e) => {
                        handleSmoothScroll('testimonials')(e);
                        closeDrawer();
                      }}
                    >
                      Testimonials
                    </NavLink>
                    <NavLink
                      href="#press-coverage"
                      className="nav-link px-0 text-secondary"
                      onClick={(e) => {
                        handleSmoothScroll('press-coverage')(e);
                        closeDrawer();
                      }}
                    >
                      Press Coverage
                    </NavLink>
                  </>
                )}
                <Nav.Item className="mt-3">
                  <LoginButton onClick={() => {
                    handleShowLogin();
                    closeDrawer();
                  }} />
                </Nav.Item>
              </>
            )}
          </Nav>
        </div>
      </div>

      {/* Login Modal - Only render when ready */}
      {/* {modalReady && (
        <Modal
          show={showLoginModal}
          onHide={handleHideLogin}
          dialogClassName="d-flex justify-content-center"
          centered
          contentClassName="w-dialog d-flex justify-content-center align-items-center user-select-none"
          backdrop={true}
          backdropClassName="login-modal-backdrop"
          animation={true}
          enforceFocus={false}
          restoreFocus={false}
        >
          <Modal.Body>
            <LoginPage />
          </Modal.Body>
        </Modal>
      )} */}
    </div>
  );
};

export default NavigationBar;